import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { enqueueSubmission } from '../utils/offlineQueue';

async function resizeImage(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 800;
        const scale = Math.min(MAX / img.width, MAX / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

function itemKey(sectionIdx, itemIdx) {
  return `s${sectionIdx}_i${itemIdx}`;
}

function StatusButton({ value, current, onChange, label, colorClass }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onChange(active ? null : value)}
      className={`flex-1 py-2.5 text-sm font-semibold rounded-lg border transition-all ${
        active
          ? `${colorClass} border-transparent`
          : 'bg-gray-700 border-gray-600 text-gray-400 hover:border-gray-500'
      }`}
    >
      {label}
    </button>
  );
}

export default function ChecklistRunner({ sections, templateName, checklistType, templateId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { membership } = useOrg();

  const [responses, setResponses] = useState({});
  const [assets, setAssets] = useState([]);
  const [assetId, setAssetId] = useState('');
  const [driverName, setDriverName] = useState('');
  const [odometer, setOdometer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [error, setError] = useState('');
  const [savedOffline, setSavedOffline] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    if (!membership?.org_id) return;
    const cacheKey = `gc_assets_${membership.org_id}`;
    const cached = (() => { try { return JSON.parse(localStorage.getItem(cacheKey)); } catch { return null; } })();
    if (cached) setAssets(cached);
    supabase
      .from('assets')
      .select('id, name, type, identifier')
      .eq('org_id', membership.org_id)
      .eq('active', true)
      .order('name')
      .then(({ data, error }) => {
        if (data && !error) {
          setAssets(data);
          try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
        }
      });
  }, [membership?.org_id]);

  const setResponse = useCallback((key, field, value) => {
    setResponses((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
  }, []);

  const handlePhotoCapture = useCallback(async (e, key) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const base64 = await resizeImage(file);
    setResponse(key, 'photo', base64);
  }, [setResponse]);

  const totalItems = sections.reduce((sum, s) => sum + s.items.length, 0);
  const answeredItems = Object.values(responses).filter((r) => r?.status).length;
  const progress = totalItems > 0 ? Math.round((answeredItems / totalItems) * 100) : 0;

  const computeStatus = () =>
    Object.values(responses).some((r) => r?.status === 'fail') ? 'fail' : 'pass';

  const buildDefects = () => {
    const defects = [];
    sections.forEach((section, sIdx) => {
      section.items.forEach((item, iIdx) => {
        const r = responses[itemKey(sIdx, iIdx)];
        if (r?.status === 'fail') {
          defects.push({ section: section.title, item, notes: r.notes || '' });
        }
      });
    });
    return defects;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!driverName.trim()) {
      setError('Please enter the operator / driver name.');
      return;
    }
    if (answeredItems === 0) {
      setError('Please complete at least one checklist item.');
      return;
    }
    setError('');
    setSubmitting(true);

    const payload = {
      org_id: membership.org_id,
      user_id: user.id,
      checklist_type: checklistType,
      template_id: templateId || null,
      vehicle_id: assetId || null,
      driver_name: driverName.trim(),
      start_odometer: odometer.trim() || null,
      responses,
      defects: buildDefects(),
      overall_status: computeStatus(),
    };

    if (!navigator.onLine) {
      enqueueSubmission(payload);
      setSavedOffline(true);
      setSubmitting(false);
      return;
    }

    const { data, error: dbError } = await supabase
      .from('submissions')
      .insert(payload)
      .select('id')
      .single();

    if (dbError) {
      setError(`Failed to save: ${dbError.message}`);
      setSubmitting(false);
      return;
    }

    setSubmittedId(data.id);
    setSubmitted(true);
    setSubmitting(false);
  };

  if (savedOffline) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-sm w-full text-center border border-yellow-500/30">
          <div className="text-4xl mb-4">📶</div>
          <h2 className="text-white text-xl font-bold mb-2">Saved Offline</h2>
          <p className="text-gray-400 text-sm mb-6">
            No network detected. Your submission is queued and will sync automatically when you're back online.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (submitted) {
    const hasFail = computeStatus() === 'fail';
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className={`bg-gray-800 rounded-2xl p-8 max-w-sm w-full text-center border ${
          hasFail ? 'border-red-500/30' : 'border-green-500/30'
        }`}>
          <div className="text-5xl mb-4">{hasFail ? '⚠️' : '✅'}</div>
          <h2 className={`text-xl font-bold mb-2 ${hasFail ? 'text-red-400' : 'text-green-400'}`}>
            {hasFail ? 'Checklist FAILED' : 'Checklist PASSED'}
          </h2>
          <p className="text-gray-400 text-sm mb-6">
            {hasFail
              ? `${buildDefects().length} defect(s) recorded. Report to your supervisor.`
              : 'All items checked. Safe to proceed.'}
          </p>
          <div className="space-y-3">
            {submittedId && (
              <button
                onClick={() => navigate(`/submissions/${submittedId}`)}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-xl"
              >
                View Report
              </button>
            )}
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 rounded-xl"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="pb-24">
      {/* Progress bar */}
      <div className="bg-gray-800 px-4 py-3 border-b border-gray-700">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-gray-400 text-xs">{answeredItems}/{totalItems} items</span>
          <span className="text-orange-400 text-xs font-semibold">{progress}%</span>
        </div>
        <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-orange-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-6">
        {/* Submission details */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 space-y-3">
          <h3 className="text-white font-semibold text-sm uppercase tracking-wide">
            Submission Details
          </h3>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Operator / Driver Name *</label>
            <input
              type="text"
              value={driverName}
              onChange={(e) => setDriverName(e.target.value.slice(0, 100))}
              placeholder="Full name"
              className="w-full bg-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Asset / Vehicle</label>
            <select
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              className="w-full bg-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">— Select asset —</option>
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.identifier ? ` (${a.identifier})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1">Start Odometer (km)</label>
            <input
              type="number"
              value={odometer}
              onChange={(e) => setOdometer(e.target.value)}
              placeholder="e.g. 128450"
              min="0"
              className="w-full bg-gray-700 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        </div>

        {/* Section tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {sections.map((section, sIdx) => {
            const allAnswered = section.items.every((_, iIdx) => responses[itemKey(sIdx, iIdx)]?.status);
            const hasFail = section.items.some((_, iIdx) => responses[itemKey(sIdx, iIdx)]?.status === 'fail');
            return (
              <button
                key={sIdx}
                type="button"
                onClick={() => setActiveSection(sIdx)}
                className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  activeSection === sIdx
                    ? 'bg-orange-500 border-orange-500 text-white font-semibold'
                    : hasFail
                    ? 'border-red-500/50 text-red-400 bg-red-500/10'
                    : allAnswered
                    ? 'border-green-500/50 text-green-400 bg-green-500/10'
                    : 'border-gray-600 text-gray-400 bg-gray-800'
                }`}
              >
                {sIdx + 1}. {section.title.split(' ')[0]}
              </button>
            );
          })}
        </div>

        {/* Active section */}
        {sections[activeSection] && (
          <div className="space-y-3">
            <h2 className="text-orange-400 font-semibold text-sm uppercase tracking-wide">
              {sections[activeSection].title}
            </h2>
            {sections[activeSection].items.map((item, iIdx) => {
              const key = itemKey(activeSection, iIdx);
              const response = responses[key] || {};
              const status = response.status;
              const showExtra = status === 'fail' || status === 'na';
              return (
                <div
                  key={key}
                  className={`bg-gray-800 border rounded-xl p-4 space-y-3 transition-colors ${
                    status === 'fail' ? 'border-red-500/50'
                    : status === 'pass' ? 'border-green-500/30'
                    : status === 'na' ? 'border-gray-600'
                    : 'border-gray-700'
                  }`}
                >
                  <p className="text-white text-sm leading-snug">{item}</p>
                  <div className="flex gap-2">
                    <StatusButton value="pass" current={status} onChange={(v) => setResponse(key, 'status', v)} label="Pass" colorClass="bg-green-500/20 text-green-400 border-green-500" />
                    <StatusButton value="fail" current={status} onChange={(v) => setResponse(key, 'status', v)} label="Fail" colorClass="bg-red-500/20 text-red-400 border-red-500" />
                    <StatusButton value="na" current={status} onChange={(v) => setResponse(key, 'status', v)} label="N/A" colorClass="bg-gray-600 text-gray-300 border-gray-500" />
                  </div>
                  {showExtra && (
                    <div className="space-y-2">
                      <textarea
                        value={response.notes || ''}
                        onChange={(e) => setResponse(key, 'notes', e.target.value.slice(0, 500))}
                        placeholder="Notes (required for fail)"
                        rows={2}
                        className="w-full bg-gray-700 text-white text-sm rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 placeholder-gray-500"
                      />
                      <label className="text-gray-500 text-xs cursor-pointer hover:text-gray-300 flex items-center gap-1.5">
                        <span>📷</span>
                        <span>{response.photo ? 'Photo attached ✓' : 'Add photo'}</span>
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhotoCapture(e, key)} />
                      </label>
                      {response.photo && (
                        <div className="flex items-center gap-2">
                          <img src={response.photo} alt="Attached" className="w-20 h-20 object-cover rounded-lg border border-gray-600" />
                          <button type="button" onClick={() => setResponse(key, 'photo', null)} className="text-red-400 text-xs hover:text-red-300">Remove</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Section nav */}
        <div className="flex gap-3">
          {activeSection > 0 && (
            <button type="button" onClick={() => setActiveSection((s) => s - 1)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-xl">
              ← Previous
            </button>
          )}
          {activeSection < sections.length - 1 && (
            <button type="button" onClick={() => setActiveSection((s) => s + 1)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 rounded-xl">
              Next →
            </button>
          )}
        </div>

        {error && (
          <div className="bg-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 border border-red-500/30">
            {error}
          </div>
        )}
      </div>

      {/* Submit bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 px-4 py-4 z-10">
        <div className="max-w-lg mx-auto">
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold py-4 rounded-xl text-base transition-colors"
          >
            {submitting ? 'Submitting…' : `Submit ${templateName}`}
          </button>
        </div>
      </div>
    </form>
  );
}
